import axios from 'axios';
import { API_URL, BFF_API } from '../config';

// Detailed tool descriptions for better AI understanding
const createPaymentLinkDescription = `
This tool will create and send a payment link to customers.

<request_examples>
- Create a payment link of 35 euros and send to email@mail.com
- Send a payment link of 1.25 EUR to +351 123456 12326 with reference "abc123"
- Create a payment link that expires in 30 minutes and send to email@mail.com. The amount is 87.00 USD.
</request_examples>

<notes>
- User's confirmation is not required for this operation.
</notes>
`;

const createRefundDescription = `
This tool initiates a refund for a specified transaction.

<request_examples>
- Refund the payment 1ca5d1ad-713c-4e62-b171-329853d09e41
- Refund 5 euros of the transaction: 1da9cfd1-127b-45c1-be03-9b66b89c9dd8
- Can you return 23.25 USD to the customer? The id of the payment is 8719f006-3e26-42ad-8182-0b7854476a49
</request_examples>

<notes>
- User's confirmation is required for this operation.
</notes>

<response_fields_description>
- Success: "True" when refund was successfuly processed. 
- TransactionId: Unique identifier for the refund transaction.
</response_fields_description>
`;

const retrieveOrderDescription = `
This tool retrieves the complete details of a specific order / payment link and its associated transactions if any.

<request_examples>
- Retrieve the order details, id: 1ca5d1ad-713c-4e62-b171-329853d09e41
- What is the status of the payment link 1da9cfd1-127b-45c1-be03-9b66b89c9dd8?
- Was the order 8719f006-3e26-42ad-8182-0b7854476a49 refund succesfully processed?
- When did the order 8719f006-3e26-42ad-8182-0b7854476a81 expire?
</request_examples>

<notes>
- User's confirmation is not required for this operation.
</notes>

<response_fields_description>
- orderId: Unique identifier for the order
- orderRef: Reference code provided by the merchant
- orderDate: Creation timestamp of the order
- expiresAt: Expiration timestamp of the order
- status: Current status of the order (see status codes below)
- checkoutUrl: URL where shoppers can complete payment
- amount: Monetary value of the order
- currency: Currency code for the transaction
- transactions: Array of associated transaction objects
- shopper: Object containing shopper details:
  * customReference: Reference ID for the shopper
  * shopperId: Unique identifier for the shopper
  * firstName: Shopper's first name
  * lastName: Shopper's last name
- orderOptions: Object with configuration details:
  * culture: Locale setting for the order
  * expiresIn: Duration until expiration
  * notifyBy: Notification preferences including email, SMS, and WhatsApp options
</response_fields_description>

<order_status>
- Created: Order has no payments associated to it or all payment attempts have failed.
- Pending: Order has a payment associated to it which is in progress but not concluded.
- Paid: Order has a payment associated to it with final status 'Success'.
- Canceled: This status can occur by system routines, shopper action by clicking on 'cancel payment' button in the hosted page or by calling the order cancel endpoint.
- Expired: This status occurs by system routines that checks for payment abandonment or in case an expiration date is sent in the create order request.
- Refunded: In case a successful payment is refunded, its order will have the 'Refunded' status.
</order_status>
`;

const retrieveTransactionDescription = `
This tool retrieves the detailed information about a specific transaction.

<request_examples>
- Retrieve the payment details, id: 1ca5d1ad-713c-4e62-b171-329853d09e41
- What is the status of the transaction 1da9cfd1-127b-45c1-be03-9b66b89c9dd8?
- Was the refund 8719f006-3e26-42ad-8182-0b7854476a49 succesfully processed?
</request_examples>

<notes>
- User's confirmation is not required for this operation.
</notes>

<response_fields_description>
- transactionId: Unique identifier for the transaction
- amount: Formatted monetary value (e.g., "30.00")
- isoAmount: Integer amount in smallest currency unit (e.g., cents)
- currency: Three-letter currency code (e.g., "EUR")
- status: Transaction status (Success, Processing, Failed, etc.)
- paymentMethod: Method type (Card, Wallet, OnlineTransfer, etc.)
- operationType: Type of operation (Payment, Refund, etc.)
- brand: Payment brand identifier (VISA, MASTER, MBWAY, PIX, etc.)
- acquirer: Payment processor name (WORLDLINE, SIBS, etc.)
- createdDate: Timestamp when transaction was created
- transactionDate: Timestamp when transaction was processed
- capturedDate: Timestamp when funds were captured
- personId: Merchant identifier
- expiresAt: Expiration timestamp
- application: Identifier of the application
- market: Market (contry) identifier
- orderRef: Reference code for the associated order
- authorizationCode: Authorization code (for card payments)
- referencedTransactions: Array of linked transactions (for refunds)

Payment method specific fields:
**Card payments:**
- maskedCardNumber: Partially hidden card number (e.g., "555534******1115")
- card: Object containing:
  - holder: Cardholder name
  - number: Masked card number
  - expiresAt: Card expiration date (MM/YY format)
  - countryCode: Country code of issuer

**MBWAY payments:**
- mbWay: Object containing:
  - entityCode: MBWAY entity code
  - phoneCountryCode: Country code for phone
  - phoneNumber: Phone number

**MULTIBANCO payments:**
- multibanco: Object containing reference details
</response_fields_description>
`;

const searchOrdersDescription = `
This tool searches for orders / payment links using various filter criteria.

<request_examples>
- Retrieve the orders from yesterday.
- Search all paid payment links from last week.
- Get the orders from dd/MM/yyyy to dd/MM/yyyy.
- Find the order created two days ago with reference ABC123.
- Has the order with reference ABC123, created last month, been paid?
</request_examples>

<notes>
- User's confirmation is not required for this operation.
- You are only allowed to search for orders within a maximum date range of 1 month. If a user requests a search for a period longer than 1 month, politely inform the user that the requested date range exceeds the allowed limit and that you can only search within a 1-month window.
- 'x-total-orders-count' represents the total number of orders matching the user's request. Always include in the message the total number of orders found. If multiple orders are found, suggest the user for more specific details.
</notes>

<response_fields_description>
- x-total-orders-count: Total number of orders matching the user's request. 
- data: Array of order objects containing:
  - id: Unique identifier for the order
  - orderRef: Reference code for the order
  - orderDate: Date when the order was created
  - status: Current status of the order
  - currency: Currency code
  - isoAmount: Amount in the smallest currency unit
  - channel: Source channel for the order
  - storeId: ID of the store that processed the order
  - storeName: Name of the store that processed the order
</response_fields_description>
`;

const exportOrdersDescription = `
This tool exports orders to CSV or XLSX format using various filter criteria and sends the file to the user's logged-in email address.

<request_examples>
- Export the orders from yesterday in CSV format.
- Send to my email all the paid orders from last week in a excel file (xlsx).
- Download the canceled orders from dd/MM/yyyy to dd/MM/yyyy.
</request_examples>

<notes>
- User's confirmation is not required for this operation.
</notes>

<response_fields_description>
- success: Boolean indicating if the export request was submitted successfully
- message: Description of the export result
</response_fields_description>
`;

// Tool definitions shared between Claude and OpenAI
export const tools = [
  {
    name: 'create_payment_link',
    description: createPaymentLinkDescription,
    parameters: {
      type: 'object',
      properties: {
        personId: {
          type: 'integer',
          description:
            'The person / store identification which the payment link will be created.',
        },
        isoAmount: {
          type: 'integer',
          description:
            'Specifies the monetary value to be charged via the payment link in ISO 4217 standard.',
        },
        currency: {
          type: 'string',
          description: 'The currency code of the payment (e.g. EUR).',
        },
        orderRef: {
          type: 'string',
          description: 'Your reference for this order, which has a default value dynamically generated (uuid).',
        },
        shopper: {
          type: 'object',
          properties: {
            firstName: {
              type: 'string',
              description: "The shopper's first name.",
            },
            lastName: {
              type: 'string',
              description: "The shopper's last name.",
            },
          },
          description: 'The shopper information.',
        },
        orderOptions: {
          type: 'object',
          properties: {
            notifyBy: {
              type: 'object',
              properties: {
                email: {
                  type: 'object',
                  properties: {
                    address: {
                      type: 'string',
                      description:
                        'The email address to send notifications to.',
                    },
                  },
                },
                sms: {
                  type: 'object',
                  properties: {
                    phoneCountryCode: {
                      type: 'integer',
                      description: 'The phone country code.',
                    },
                    phoneNumber: {
                      type: 'string',
                      description: 'The phone number.',
                    },
                  },
                },
                whatsapp: {
                  type: 'object',
                  properties: {
                    phoneCountryCode: {
                      type: 'integer',
                      description: 'The phone country code.',
                    },
                    phoneNumber: {
                      type: 'string',
                      description: 'The phone number.',
                    },
                  },
                },
              },
              description: 'Notification options for the payment link.',
            },
            redirectUrl: {
              type: 'string',
              description: 'URL to redirect the shopper after payment.',
            },
            culture: {
              type: 'string',
              description:
                'The culture code for the payment page (e.g. en-US).',
            },
            expiresIn: {
              type: 'string',
              description:
                'The duration for which the payment link is valid (format: hh:mm:ss).',
            },
          },
          description: 'CConfiguration options including notification preferences, redirect URL, culture code, and expiration time.',
        },
        paymentOptions: {
          type: 'object',
          properties: {
            allowedPaymentMethods: {
              type: 'array',
              items: {
                type: 'string',
                enum: [
                  'CARD',
                  'MBWAY',
                  'SIBS_MULTIBANCO',
                  'IDEAL',
                  'PAYPAL',
                  'MULTICAIXA_REF',
                  'SEPA',
                  'KLARNA',
                  'FLOA3X',
                  'FLOA4X',
                ],
              },
              description: 'List of payment methods that should be available.',
            },
          },
          description: 'Payment-specific options.',
        },
      },
      required: ['isoAmount', 'currency', 'personId'],
    },
  },
  {
    name: 'create_refund',
    description: createRefundDescription,
    parameters: {
      type: 'object',
      properties: {
        transactionId: {
          type: 'string',
          description: 'The ID of the transaction to refund.',
        },
        isoAmount: {
          type: 'integer',
          description:
            'Amount to refund, in the smallest currency unit as per ISO 4217 standards.',
        },
      },
      required: ['transactionId', 'isoAmount'],
    },
  },
  {
    name: 'retrieve_order',
    description: retrieveOrderDescription,
    parameters: {
      type: 'object',
      properties: {
        orderId: {
          type: 'string',
          description: 'The ID of the order to retrieve.',
        },
      },
      required: ['orderId'],
    },
  },
  {
    name: 'retrieve_transaction',
    description: retrieveTransactionDescription,
    parameters: {
      type: 'object',
      properties: {
        transactionId: {
          type: 'string',
          description: 'The ID of the transaction to retrieve.',
        },
      },
      required: ['transactionId'],
    },
  },
  {
    name: 'search_orders',
    description: searchOrdersDescription,
    parameters: {
      type: 'object',
      properties: {
        OrderDateFrom: {
          type: 'string',
          description: 'The start date for the orders search in ISO format with the timezone indicator (e.g., "2025-04-29T10:00:00.000+02:00").',
        },
        OrderDateTo: {
          type: 'string',
          description: 'The end date for the orders search in ISO format with the timezone indicator (e.g., "2025-04-29T22:00:00.000+02:00").',
        },
        StoreId: {
          type: 'array',
          items: {
            type: 'integer'
          },
          description: 'Array of store IDs to filter by.',
        },
        OrderRef: {
          type: 'string',
          description: 'The order reference number.',
        },
        Pnr: {
          type: 'string',
          description: 'The passenger name record for flight bookings.',
        },
        ClientFrequentFlightNumber: {
          type: 'string',
          description: 'The client\'s frequent flyer number.',
        },
        ShopperEmail: {
          type: 'string',
          description: 'The email address of the shopper.',
        },
        IsProduction: {
          type: 'boolean',
          description: 'Filter by production status.',
        },
        Status: {
          type: 'array',
          items: {
            type: 'string'
          },
          description: 'List of order / payment link status to filter by (Created, Pending, Paid, Refunded, Expired, Canceled).',
        },
        Country: {
          type: 'array',
          items: {
            type: 'string'
          },
          description: 'List of market id / country codes (three letter code) to filter by.',
        },
        ApplicationId: {
          type: 'array',
          items: {
            type: 'string'
          },
          description: 'List of application IDs to filter by.',
        },
        Amount: {
          type: 'number',
          description: 'Filter by monetary value in ISO 4217 standard. If user says 5 euros, amount must be 500.',
        },
        Currency: {
          type: 'string',
          description: 'Filter by the currency code (e.g., EUR, USD).',
        }
      },
      required: ['OrderDateFrom', 'OrderDateTo', 'StoreId'],
    },
  },
  {
    name: 'export_orders',
    description: exportOrdersDescription,
    parameters: {
      type: 'object',
      properties: {
        outputType: {
          type: 'string',
          enum: ['csv', 'xlsx'],
          description: 'The format of the export file (csv or xlsx).',
        },
        OrderDateFrom: {
          type: 'string',
          description: 'The start date for the orders export in ISO format with the timezone indicator (e.g., "2025-04-29T10:00:00.000+02:00").',
        },
        OrderDateTo: {
          type: 'string',
          description: 'The end date for the orders export in ISO format with the timezone indicator (e.g., "2025-04-29T22:00:00.000+02:00").',
        },
        StoreId: {
          type: 'array',
          items: {
            type: 'integer'
          },
          description: 'Array of store IDs to filter by.',
        },
        Amount: {
          type: 'number',
          description: 'Filter by monetary value in smallest currency unit (e.g., 500 for 5.00 EUR).',
        },
        Currency: {
          type: 'array',
          items: {
            type: 'string'
          },
          description: 'Array of currency codes to filter by (e.g., ["EUR", "USD"]).',
        },
        Country: {
          type: 'array',
          items: {
            type: 'string'
          },
          description: 'Array of country codes to filter by (e.g., ["PRT", "ESP"]).',
        },
        ApplicationId: {
          type: 'array',
          items: {
            type: 'string'
          },
          description: 'Array of application IDs to filter by (e.g., ["CALLCENTER", "FLYGROUPS"]).',
        },
        OrderRef: {
          type: 'string',
          description: 'Filter by order reference.',
        },
        Pnr: {
          type: 'string',
          description: 'Filter by passenger name record.',
        },
        ClientFrequentFlightNumber: {
          type: 'string',
          description: 'Filter by client frequent flight number.',
        },
        ShopperEmail: {
          type: 'string',
          description: 'Filter by shopper email address.',
        },
        Status: {
          type: 'array',
          items: {
            type: 'string'
          },
          description: 'Array of order statuses to filter by (e.g., ["Paid", "Pending"]).',
        },
        IsProduction: {
          type: 'boolean',
          description: 'Filter by production environment status.',
        },
      },
      required: ['outputType', 'OrderDateFrom', 'OrderDateTo', 'StoreId'],
    },
  },
];

// Function to execute API calls based on function name and arguments
export const executeFunction = async (functionName, args, accessToken) => {
  const baseUrl = `${API_URL}/api/v2`;
  const bffBaseUrl = `${BFF_API}`;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'x-paybyrd-ai': 'true',
  };

  try {
    let response;
    switch (functionName) {
      case 'create_payment_link':
        try {
          response = await axios.post(`${baseUrl}/orders`, args, { headers });

          if (response.data.checkoutUrl) {
            return JSON.stringify({
              checkoutUrl: response.data.checkoutUrl,
              orderId: response.data.orderId,
            });
          } else {
            return JSON.stringify(response.data);
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return JSON.stringify(
              `Failed to create payment link: ${error.response?.data?.message || error.message}`
            );
          }
          return JSON.stringify('Failed to create payment link');
        }

      case 'create_refund':
        try {
          const { transactionId, ...refundBody } = args;
          response = await axios.post(
            `${baseUrl}/refund/${transactionId}`,
            refundBody,
            { headers }
          );

          if (response.data.code === 'BYRD200') {
            return JSON.stringify({
              success: true,
              transactionId: response.data.transactionId,
              code: response.data.code,
              description: response.data.description,
            });
          } else {
            return JSON.stringify({
              success: false,
              transactionId: response.data.transactionId,
              code: response.data.code,
              description:
                response.data.description || 'Refund operation failed',
            });
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return JSON.stringify(
              `Failed to create refund: ${error.response?.data?.message || error.message}`
            );
          }
          return JSON.stringify('Failed to create refund');
        }

      case 'retrieve_order':
        try {
          const { orderId } = args;
          response = await axios.get(`${baseUrl}/orders/${orderId}`, {
            headers,
          });

          if (response.status === 200) {
            // Extract only the requested fields and all transaction fields
            const data = response.data;
            const filteredData = {
              orderDate: data.orderDate,
              expiresAt: data.expiresAt,
              status: data.status,
              checkoutUrl: data.checkoutUrl,
              transactions: data.transactions || [],
              orderId: data.orderId,
              amount: data.amount,
              currency: data.currency,
              orderRef: data.orderRef,
              shopper: data.shopper
                ? {
                    customReference: data.shopper.customReference,
                    shopperId: data.shopper.shopperId,
                    firstName: data.shopper.firstName,
                    lastName: data.shopper.lastName,
                  }
                : null,
              orderOptions: data.orderOptions
                ? {
                    culture: data.orderOptions.culture,
                    expiresIn: data.orderOptions.expiresIn,
                    notifyBy: data.orderOptions.notifyBy
                      ? {
                          email: data.orderOptions.notifyBy.email
                            ? {
                                address:
                                  data.orderOptions.notifyBy.email.address,
                              }
                            : null,
                          whatsApp: data.orderOptions.notifyBy.whatsApp
                            ? {
                                firstName:
                                  data.orderOptions.notifyBy.whatsApp.firstName,
                                lastName:
                                  data.orderOptions.notifyBy.whatsApp.lastName,
                                phoneCountryCode:
                                  data.orderOptions.notifyBy.whatsApp
                                    .phoneCountryCode,
                                phoneNumber:
                                  data.orderOptions.notifyBy.whatsApp
                                    .phoneNumber,
                              }
                            : null,
                          sms: data.orderOptions.notifyBy.sms
                            ? {
                                firstName:
                                  data.orderOptions.notifyBy.sms.firstName,
                                lastName:
                                  data.orderOptions.notifyBy.sms.lastName,
                                phoneCountryCode:
                                  data.orderOptions.notifyBy.sms
                                    .phoneCountryCode,
                                phoneNumber:
                                  data.orderOptions.notifyBy.sms.phoneNumber,
                              }
                            : null,
                        }
                      : null,
                  }
                : null,
            };

            return JSON.stringify(filteredData);
          } else {
            return JSON.stringify('Failed to retrieve order details');
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return JSON.stringify(
              `Failed to retrieve order: ${error.response?.data?.message || error.message}`
            );
          }
          return JSON.stringify('Failed to retrieve order');
        }

      case 'retrieve_transaction':
        try {
          const { transactionId } = args;

          response = await axios.get(
            `${baseUrl}/transactions/${transactionId}`,
            { headers }
          );

          if (response.status === 200) {
            const transaction = {
              transactionId: response.data.transactionId,
              amount: response.data.amount,
              isoAmount: response.data.isoAmount,
              currency: response.data.currency,
              status: response.data.status,
              paymentMethod: response.data.paymentMethod,
              operationType: response.data.operationType,
              brand: response.data.brand,
              acquirer: response.data.acquirer,
              createdDate: response.data.createdDate,
              transactionDate: response.data.transactionDate,
              capturedDate: response.data.capturedDate,
              personId: response.data.personId,
              expiresAt: response.data.expiresAt,
              application: response.data.applicationId,
              market: response.data.marketId,
              orderRef: response.data.orderRef,
              authorizationCode: response.data.authorizationCode,
              maskedCardNumber: response.data.maskedCardNumber,
              multibanco: response.data.multibanco,
              mbWay: response.data.mbWay,
              card: response.data.card
                ? {
                    holder: response.data.card.holder,
                    number: response.data.card.number,
                    expiresAt: response.data.card.expiresAt,
                    countryCode: response.data.card.countryCode,
                  }
                : null,
            };

            // Return all fields, including those specific to payment method
            return JSON.stringify(transaction);
          } else {
            return JSON.stringify(response.data);
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return JSON.stringify(
              `Failed to retrieve transaction: ${error.response?.data?.message || error.message}`
            );
          }
          return JSON.stringify('Failed to retrieve transaction details');
        }

      case 'search_orders':
        try {
          // Set default/required query parameters
          const queryParams = {
            Page: 1,
            PageSize: 5,
            ...args
          };

          // Build query string
          const queryString = Object.entries(queryParams)
            .flatMap(([key, value]) => {
              if (Array.isArray(value)) {
                return value.map(item => `${key}[]=${encodeURIComponent(item)}`);
              }
              return [`${key}=${encodeURIComponent(value)}`];
            })
            .join('&');

          response = await axios.get(
            `${bffBaseUrl}/orders/?${queryString}`,
            { headers }
          );

          if (response.status === 200 && response.data) {
            // Get total count from header
            const totalCount = response.headers['x-total-item-count'] || 0;            
            
            // Extract only the specific fields from each order
            const filteredOrders = (response.data.data || []).map(order => ({
              channel: order.channel || "",
              currency: order.currency || "",
              id: order.id || "",
              isoAmount: order.isoAmount || 0,
              orderDate: order.orderDate || "",
              orderRef: order.orderRef || "",
              status: order.status || "",
              storeId: order.storeId || 0,
              storeName: order.storeName || ""
            }));
            
            // Format the response with filtered data and total count
            const formattedResponse = {
              "x-total-orders-count": parseInt(totalCount, 10),
              data: filteredOrders,              
            };
            
            return JSON.stringify(formattedResponse);
          } else {
            return JSON.stringify({ error: 'Failed to search orders', data: [] });
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return JSON.stringify(
              `Failed to search orders: ${error.response?.data?.message || error.message}`
            );
          }
          return JSON.stringify('Failed to search orders');
        }

      case 'export_orders':
        try {
          response = await axios.post(
            `${bffBaseUrl}/reports/orders`,
            args,
            { headers }
          );

          if (response.status === 202) {
            return JSON.stringify({
              success: true,
              message: 'Orders export request submitted successfully. The report will be sent to your email.',
            });
          } else {
            return JSON.stringify({
              success: false,
              message: 'Failed to export orders',
              error: response.data
            });
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            return JSON.stringify({
              success: false,
              message: `Failed to export orders: ${error.response?.data?.message || error.message}`,
              error: error.response?.data
            });
          }
          return JSON.stringify({
            success: false,
            message: 'Failed to export orders',
            error: error.message
          });
        }

      default:
        return JSON.stringify(`Unknown function: ${functionName}`);
    }
  } catch (error) {
    console.error(`Error in executeFunction for ${functionName}:`, error);
    return JSON.stringify(`Error executing function: ${error.message}`);
  }
};
